import { useAuth } from "@/src/contexts/auth.context";
import { getRefreshToken } from "@/src/services/api/auth.api";
import { useGetOrganizationColorQuery } from "@/src/services/organization/organization.hooks";
import {
  useOrganizationsQuery,
  useSwitchOrgMutation,
} from "@/src/services/uam/permissions.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import { derivePalette } from "@/src/utils/derive-palette";
import { useRouter } from "expo-router";
import { CloseCircle } from "iconsax-react-nativejs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  Text,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import Toast from "react-native-toast-message";

type Props = Readonly<{
  visible: boolean;
  onClose: () => void;
}>;

type ResolvedMembership = Readonly<{
  organizationId: string;
  orgName: string;
  role: string;
}>;

export default function OrgSwitcherSheet({ visible, onClose }: Props) {
  const { themeVars, varStyle, setPrimaryPalette } = useTheme();
  const { t } = useTranslation();
  const { user, refreshAuth } = useAuth();
  const router = useRouter();

  const { mutateAsync: switchOrg, isPending: isSwitchOrgPending } =
    useSwitchOrgMutation();
  const { data: organizationsData, isLoading: isOrgsLoading } =
    useOrganizationsQuery();



  const memberships = user?.memberships ?? [];
  const resolvedMemberships: ReadonlyArray<ResolvedMembership> = memberships
    .map((membership) => {
      const orgName = organizationsData?.find(
        (org) => org.itemId === membership.organizationId,
      )?.name;
      const role = membership?.roles?.[0];

      if (!orgName || !role) return null;
      if (membership.organizationId === "default") return null;

      return {
        organizationId: membership.organizationId,
        orgName,
        role: role
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      };
    })
    .filter(
      (membership): membership is ResolvedMembership => membership !== null,
    );

  const handleSwitch = async (orgId: string) => {
    try {
      const refreshToken = (await getRefreshToken()) ?? "";
      if (Platform.OS !== "web" && !refreshToken)
        throw new Error("Missing refresh token");

      await switchOrg({ orgId, refreshToken });

      // Skip the auth-context auto-redirect so it doesn't race with the
      // explicit navigation below (double REPLACE → "not handled by navigator").
      await refreshAuth(true);
      onClose();
      await handleOrgThemeAndNavigation()
      router.replace("/feeds");
    } catch {
      Toast.show({ type: "error", text1: t("ERROR_SWITCH_ORG_FAILED") });
    }
  };
  const { refetch: refetchOrgColor } = useGetOrganizationColorQuery({
    enabled: false,
  });
  const handleOrgThemeAndNavigation = async () => {
    const { data: newOrgColor } = await refetchOrgColor();
    if (newOrgColor?.primaryColor) {
      try {
        const primaryColorString = String(newOrgColor.primaryColor).trim();
        if (primaryColorString) {
          setPrimaryPalette(derivePalette(primaryColorString));
        } else {
          console.warn("Primary color is empty after conversion");
        }
      } catch (e) {
        console.error("Failed to derive palette from color:", e);
      }
    } else {
      console.warn("No primaryColor found in organization data");
    }

    router.replace("/feeds");
  };
  const isLoading = isOrgsLoading || isSwitchOrgPending;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={varStyle} className="flex-1 bg-black/40 justify-end items-center">
          <TouchableWithoutFeedback>
            <View className="bg-chalk rounded-t-3xl pb-3 w-full md:w-[460px]">
              <View className="w-8 h-1 bg-ash-3 rounded-full self-center mt-3" />

              <View className="flex-row items-center justify-between px-4 pt-3 pb-2.5">
                <Text className="text-sm font-semibold text-gray-dark">
                  {t("LABEL_SWITCH_CLUB")}
                </Text>
                <Pressable onPress={onClose} hitSlop={8} className="active:opacity-70 hover:bg-ash-3 hover:rounded-full">
                  <CloseCircle size={24} color={themeVars["--color-primary-default"] as string} />
                </Pressable>
              </View>

              <View className="h-px bg-ash mx-4 mb-1.5" />

              {isLoading ? (
                <View className="py-6 items-center">
                  <ActivityIndicator size="small" color={themeVars["--color-primary-default"] as string} />
                </View>
              ) : (
                <View className="px-2 gap-0.5">
                  {resolvedMemberships.map((membership) => (
                    <Pressable
                      key={membership.organizationId}
                      onPress={() => handleSwitch(membership.organizationId)}
                      disabled={isSwitchOrgPending}
                      className="flex-row items-center gap-2.5 p-2.5 rounded-xl active:bg-ash hover:bg-ash-3"
                    >
                      <View className="flex-1">
                        <Text className="font-semibold text-gray-dark text-[13px]">
                          {membership.orgName}
                        </Text>
                        <Text className="text-[11px] text-slate mt-0.5">
                          {membership.role}
                        </Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
}
